import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle, GraduationCap, Clock } from "lucide-react";
import { api } from "@/lib/api";

const COURSE_TINT = [
  { bg: "bg-[#C7DCF7]", text: "text-[#1F2547]" },
  { bg: "bg-[#D4EDDB]", text: "text-[#1F2547]" },
  { bg: "bg-[#9F87D8]", text: "text-white" },
  { bg: "bg-[#EFE9F7]", text: "text-[#1F2547]" },
  { bg: "bg-[#1F2547]", text: "text-white" },
  { bg: "bg-[#FFE4F1]", text: "text-[#1F2547]" },
];

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/courses")
      .then((r) => setCourses(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="courses-page" className="min-h-[60vh]">
      <section className="bg-[#9F87D8]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/80">
            / Programs
          </span>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter mt-4 max-w-4xl text-white leading-[0.95]">
            Courses that meet you where you are.
          </h1>
          <p className="mt-5 text-lg md:text-xl text-white/85 font-medium max-w-2xl">
            From little learners to working professionals — every Percept
            Academy program is designed around real progress you can see.
          </p>

          <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur text-white text-sm font-bold">
            <GraduationCap className="w-4 h-4" />
            {loading ? "Loading…" : `${courses.length} active program${courses.length === 1 ? "" : "s"}`}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        {loading ? (
          <div className="text-[#5B6079] font-medium text-center py-12">
            Loading courses…
          </div>
        ) : courses.length === 0 ? (
          <div className="text-[#5B6079] font-medium text-center py-12">
            No courses to show yet. Check back soon.
          </div>
        ) : (
          <div className="space-y-8">
            {courses.map((c, idx) => {
              const tint = COURSE_TINT[idx % COURSE_TINT.length];
              const isDark = tint.text === "text-white";
              return (
                <article
                  key={c.id}
                  data-testid={`course-page-card-${c.id}`}
                  className={`group rounded-3xl brutal-shadow hover-pop overflow-hidden ${tint.bg} ${tint.text}`}
                >
                  <div className="grid md:grid-cols-5 gap-0">
                    {c.image && (
                      <div className="md:col-span-2 aspect-[5/4] md:aspect-auto overflow-hidden bg-[#EFE9F7]">
                        <img
                          src={c.image}
                          alt={c.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div
                      className={`${c.image ? "md:col-span-3" : "md:col-span-5"} p-7 md:p-10 flex flex-col justify-between gap-6`}
                    >
                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-4">
                          {c.tag && (
                            <span
                              className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full ${isDark ? "bg-white/20 backdrop-blur text-white" : "bg-white/70 backdrop-blur text-[#1F2547]"}`}
                            >
                              {c.tag}
                            </span>
                          )}
                          {c.duration && (
                            <span
                              className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full ${isDark ? "bg-white/20 backdrop-blur text-white" : "bg-[#1F2547] text-white"}`}
                            >
                              <Clock className="w-3 h-3" /> {c.duration}
                            </span>
                          )}
                        </div>
                        <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight leading-tight">
                          {c.title}
                        </h2>
                        <p
                          className={`mt-4 text-base md:text-lg font-medium leading-relaxed ${isDark ? "text-white/85" : "text-[#1F2547]/78"}`}
                        >
                          {c.description}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <Link
                          to={`/enroll?course=${c.id}`}
                          data-testid={`course-page-enrol-${c.id}`}
                          className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-bold rounded-full btn-press ${isDark ? "bg-white text-[#1F2547]" : "bg-[#1F2547] text-white"}`}
                        >
                          Enrol now <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        {c.whatsapp_link && (
                          <a
                            href={c.whatsapp_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`course-page-whatsapp-${c.id}`}
                            className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-bold rounded-full ${isDark ? "bg-white/15 backdrop-blur text-white" : "bg-white text-[#1F2547] border border-[#1F2547]/10"}`}
                          >
                            <MessageCircle className="w-4 h-4" /> WhatsApp
                          </a>
                        )}
                        {c.offer_link && (
                          <a
                            href={c.offer_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`course-page-offer-${c.id}`}
                            className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-bold rounded-full ${isDark ? "bg-white/15 backdrop-blur text-white" : "bg-[#D4EDDB] text-[#1F2547]"}`}
                          >
                            View offer
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <div className="mt-16 rounded-3xl bg-[#EFE9F7] p-8 md:p-12 text-center">
          <h3 className="font-display text-2xl md:text-3xl font-black tracking-tight text-[#1F2547]">
            Not sure which course fits?
          </h3>
          <p className="mt-2 text-[#5B6079] font-medium max-w-xl mx-auto">
            Send us a quick WhatsApp and we&apos;ll help you or your child pick
            the right program.
          </p>
          <a
            href="https://wa.me/919968110261?text=Hi%2C%20I%27m%20not%20sure%20which%20course%20to%20pick.%20Could%20you%20help%3F"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="courses-bottom-cta"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-[#9F87D8] text-white font-bold rounded-full brutal-shadow-sm btn-press"
          >
            <MessageCircle className="w-4 h-4" /> Ask on WhatsApp
          </a>
        </div>
      </section>
    </main>
  );
}
